import type { SceneObject } from '../../core/SceneObject';
import type { Interaction, InteractionAction } from '../../editor/authoring/interactionGraph';
import type { SpatialInteractionDescriptor } from './spatial-types';

/** The actions of a tool's interaction: its `actions` array, or the one action spelled on the interaction itself. */
export function interactionActions(input: Record<string, unknown>): Record<string, unknown>[] {
    if (input.actions !== undefined && input.action !== undefined) throw new Error('Use action or actions, not both');
    const actions = input.actions === undefined ? [input] : input.actions;
    if (!Array.isArray(actions) || actions.length < 1 || actions.length > 32) throw new Error('actions must contain 1–32 actions');
    if (actions.some(a => !a || typeof a !== 'object' || Array.isArray(a) || typeof a.action !== 'string')) throw new Error('Each action requires an action name');
    return actions as Record<string, unknown>[];
}

/** The event as the operation names it: `onSelect`, as the fluent builder spells it, is `select`. */
function eventName(event: unknown): Interaction['event'] {
    return (typeof event === 'string' && /^on[A-Z]/.test(event) ? event[2].toLowerCase() + event.slice(3) : event) as Interaction['event'];
}

/**
 * A tool's interaction, with its object, scope and targets resolved from
 * their refs; `resolveTarget`, when given, resolves the action targets.
 * trace-viewer checks the rest when it wires it.
 */
export function interactionFromInput(
    input: Record<string, unknown>,
    resolve: (ref: string) => SceneObject | null,
    resolveTarget: (ref: string) => SceneObject | null = resolve,
): Interaction {
    if (typeof input.object !== 'string' || !input.object) throw new Error('object is required');
    const object = resolve(input.object);
    if (!object) throw new Error(`object "${input.object}" not found`);
    const scope = input.scope === undefined ? undefined : typeof input.scope === 'string' ? resolve(input.scope) : null;
    if (scope === null) throw new Error('scope must identify an existing object');
    const actions = interactionActions(input).map((action, index) => {
        const { target, ...rest } = action;
        // A navigate has no target; a missing one is reported by the viewer.
        if (rest.action === 'navigate' || typeof target !== 'string' || !target) return rest as unknown as InteractionAction;
        try {
            const resolved = resolveTarget(target);
            if (!resolved) throw new Error(`target "${target}" not found`);
            // Typed on `target`, so a renamed viewer field fails here; the rest is the tool's unchecked input.
            const targeted: Extract<InteractionAction, { target: SceneObject }> = { ...(rest as unknown as Extract<InteractionAction, { target: SceneObject }>), target: resolved };
            return targeted;
        } catch (error) {
            throw new Error(`action ${index + 1}: ${error instanceof Error ? error.message : String(error)}`);
        }
    });
    // The fields are the tool's input, unchecked; the viewer checks them.
    return {
        object, scope, actions,
        event: eventName(input.event),
        distance: input.distance as Interaction['distance'],
        when: input.when as Interaction['when'],
        mode: input.mode as Interaction['mode'],
    };
}

/** Expand the spatial shorthand into the same actions the native input wires. */
export function spatialInteractionActions(interaction: SpatialInteractionDescriptor): Record<string, unknown>[] | undefined {
    return interaction.type === 'native_chain' ? interaction.actions
        : interaction.type === 'click_toggle' ? [{ action: 'toggleActive', target: interaction.targetId }]
        : interaction.type === 'click_set_state' ? [{ action: 'setState', target: interaction.targetId, state: interaction.stateName ?? '' }]
        : [{ action: 'setActive', target: interaction.targetId, active: true },
            ...(interaction.exclusiveGroupIds ?? []).filter(id => id !== interaction.targetId)
                .map(target => ({ action: 'setActive', target, active: false }))];
}

/** Shared builder/materializer limits, applied before scene mutation. */
export function validateSpatialInteractions(interactions: SpatialInteractionDescriptor[]): void {
    if (interactions.length > 64) throw new Error('A build supports at most 64 interactions');
    let actionCount = 0;
    for (const interaction of interactions) {
        const actions = spatialInteractionActions(interaction);
        if (actions && actions.length > 32) throw new Error('An event supports at most 32 actions');
        actionCount += actions?.length ?? 0;
        if (actionCount > 256) throw new Error('A build supports at most 256 interaction actions');
    }
}
